/*
 * Path Explorer landing: overview video band.
 *
 * - Plays the muted, looping background video only while the band is on screen
 *   and only if the visitor has not asked for reduced motion.
 * - Pauses when scrolled away to save battery and bandwidth.
 * - Provides a play/pause toggle so keyboard and assistive-tech users stay in control.
 * - Re-initialises when the theme editor re-renders the section.
 */
(function () {
  'use strict';

  function initBand(band) {
    if (band.dataset.peVideoReady === 'true') return;
    var video = band.querySelector('video');
    if (!video) return;
    band.dataset.peVideoReady = 'true';

    var toggle = band.querySelector('[data-pe-video-toggle]');
    var wantsAutoplay = band.dataset.autoplay === 'true';
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var userPaused = false;

    // Background-video hygiene regardless of how the <video> was rendered.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');

    function setState(playing) {
      band.classList.toggle('pe-video-band--playing', playing);
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute(
        'aria-label',
        playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay
      );
    }

    function tryPlay() {
      if (userPaused) return;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          setState(false);
        });
      }
    }

    video.addEventListener('play', function () {
      setState(true);
    });
    video.addEventListener('pause', function () {
      setState(false);
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused) {
          userPaused = false;
          tryPlay();
        } else {
          userPaused = true;
          video.pause();
        }
      });
    }

    setState(false);
    if (!wantsAutoplay || reduceMotion) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              tryPlay();
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(band);
    } else {
      tryPlay();
    }
  }

  /*
   * Lets the hero run up behind the announcement bar and header: measures everything in the
   * header group and exposes it as --pe-top-offset (see pe-window-refresh.css). Without JS the
   * offset stays 0 and the hero simply starts below the header.
   */
  var topObserver = null;

  function measureTopOffset() {
    var total = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach(function (el) {
      total += el.offsetHeight;
    });
    document.documentElement.style.setProperty('--pe-top-offset', total + 'px');
  }

  function initTopOffset() {
    if (!document.querySelector('.path-explorer-section .pe-hero, .pb-page .pb-hero')) return;
    measureTopOffset();
    if (topObserver || !('ResizeObserver' in window)) return;
    topObserver = new ResizeObserver(measureTopOffset);
    document.querySelectorAll('.shopify-section-group-header-group').forEach(function (el) {
      topObserver.observe(el);
    });
  }

  function init() {
    document.querySelectorAll('[data-pe-video-band]').forEach(initBand);
    initTopOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
